import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, UploadCloud } from 'lucide-react';
import toast from 'react-hot-toast';
import { mediaAPI } from '../../services/api';
import MediaAssetCard from './MediaAssetCard';
import MediaUploader from './MediaUploader';

/**
 * Modal for picking a media asset (to drop into a section/block/blog cover).
 *
 * Callers use it like:
 *   <MediaPicker
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onPick={(asset) => setHeroImage(asset.publicUrl)}
 *     folder="sections"
 *   />
 */

const FOLDERS = [
  { key: '', label: 'الكل' },
  { key: 'hero', label: 'Hero' },
  { key: 'sections', label: 'الأقسام' },
  { key: 'gallery', label: 'المعرض' },
  { key: 'blog', label: 'المدونة' },
  { key: 'offers', label: 'العروض' },
  { key: 'team', label: 'الفريق' },
  { key: 'logos', label: 'الشعارات' },
];

export default function MediaPicker({ open, onClose, onPick, folder: initialFolder = '' }) {
  const [mode, setMode] = useState('browse'); // 'browse' | 'upload'
  const [folder, setFolder] = useState(initialFolder);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const resp = await mediaAPI.list({ folder, q, page, pageSize: 24 });
      const data = resp.data?.data || resp.data;
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('فشل تحميل المكتبة');
    } finally {
      setLoading(false);
    }
  }, [folder, q, page, open]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { if (open) { setMode('browse'); setSelectedId(null); } }, [open]);

  const selectedAsset = useMemo(() => items.find((a) => a._id === selectedId) || null, [items, selectedId]);
  const totalPages = Math.max(1, Math.ceil(total / 24));

  const onAssetReady = useCallback((asset) => {
    setItems((prev) => [asset, ...prev]);
    setTotal((t) => t + 1);
    setMode('browse');
    setSelectedId(asset._id);
  }, []);

  const confirmPick = () => {
    if (!selectedAsset) return toast('اختر صورة أولاً');
    if (selectedAsset.status !== 'ready') return toast.error('الصورة لم تكتمل بعد');
    onPick?.(selectedAsset);
    onClose?.();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4"
        onClick={onClose}
        dir="rtl"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">اختيار صورة من المكتبة</h2>
              <p className="text-xs text-gray-500 mt-0.5">اختر صورة موجودة أو ارفع جديدة</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mode tabs */}
          <div className="px-6 pt-4 flex items-center gap-2 border-b border-gray-100">
            {['browse', 'upload'].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={[
                  'px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
                  mode === m ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700',
                ].join(' ')}
              >
                {m === 'browse' ? 'تصفح المكتبة' : 'رفع جديد'}
              </button>
            ))}
          </div>

          {mode === 'browse' ? (
            <>
              {/* Filters */}
              <div className="px-6 py-3 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(1); }}
                    placeholder="بحث في أسماء الملفات..."
                    className="w-full pr-10 pl-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select
                  value={folder}
                  onChange={(e) => { setFolder(e.target.value); setPage(1); }}
                  className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {FOLDERS.map((f) => (
                    <option key={f.key || 'all'} value={f.key}>{f.label}</option>
                  ))}
                </select>
              </div>

              {/* Grid */}
              <div className="flex-1 overflow-y-auto px-6 py-3">
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <UploadCloud className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm">لا توجد صور — ارفع أول صورة</p>
                    <button
                      onClick={() => setMode('upload')}
                      className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                    >
                      رفع صورة
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {items.map((asset) => (
                      <MediaAssetCard
                        key={asset._id}
                        asset={asset}
                        selected={selectedId === asset._id}
                        onSelect={() => setSelectedId(asset._id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>صفحة {page} من {totalPages}</span>
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-2 py-1 rounded-md text-gray-500 disabled:text-gray-300 hover:bg-gray-50"
                  >السابق</button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-2 py-1 rounded-md text-gray-500 disabled:text-gray-300 hover:bg-gray-50"
                  >التالي</button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >إلغاء</button>
                  <button
                    onClick={confirmPick}
                    disabled={!selectedAsset || selectedAsset.status !== 'ready'}
                    className="px-5 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-600 to-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >اختيار</button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-6 overflow-y-auto">
              <MediaUploader folder={folder || 'sections'} onAssetReady={onAssetReady} />
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
